import { useEffect, useState } from "react"
import { parseAsString, parseAsStringLiteral, useQueryState } from "nuqs"
import toast from "react-hot-toast"
import { useHotkeys } from "react-hotkeys-hook"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useLocalStorage,
} from "@aix/ui"

import { trpc } from "../lib/trpc"
import { SEO_DEFAULT_PRODUCT_KEY } from "./seoBoardHelpers"
import SEOCockpitTab from "./SEOCockpitTab"
import SEOKeywordsTab from "./SEOKeywordsTab"
import SEOOperationsTab from "./SEOOperationsTab"
import SEOPagesTab from "./SEOPagesTab"
import SEOPublishTab from "./SEOPublishTab"
import SEORunbookDrawer from "./SEORunbookDrawer"
import SEOScoreboardTab from "./SEOScoreboardTab"
import SEOTemplateLabTab from "./SEOTemplateLabTab"
import type { SeoTabValue } from "./SEOShortcutLegend"
import { SEO_TAB_VALUES, SEOShortcutLegend } from "./SEOShortcutLegend"

const TAB_TRIGGER_CLASS =
  "border-2 border-border data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs md:text-sm whitespace-nowrap px-2 md:px-4"

/**
 * SEO board (route /seo) — DB-backed programmatic SEO control plane.
 * Backend: aixinternal-api `seo` router. Spec: claude/specs/seo-platform-v2.md.
 */
export default function SEOScreen() {
  const [activeTab, setActiveTab] = useQueryState("tab", parseAsStringLiteral(SEO_TAB_VALUES).withDefault("cockpit"))
  const [productKey, setProductKey] = useQueryState("product", { defaultValue: SEO_DEFAULT_PRODUCT_KEY })
  const [selectedPageSlug, setSelectedPageSlug] = useQueryState("page", parseAsString.withDefault(""))
  const [pageMode, setPageMode] = useQueryState(
    "mode",
    parseAsStringLiteral(["inspect", "preview", "edit"] as const).withDefault("inspect"),
  )
  const [shortcutLegendOpen, setShortcutLegendOpen] = useLocalStorage("seo-shortcut-legend-open", true)
  const [pageEditorDirty, setPageEditorDirty] = useState(false)
  const [pendingTab, setPendingTab] = useState<SeoTabValue | null>(null)
  const [runbookOpen, setRunbookOpen] = useState(false)

  useEffect(() => {
    if (!pageEditorDirty) return
    const preventUnsavedUnload = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener("beforeunload", preventUnsavedUnload)
    return () => window.removeEventListener("beforeunload", preventUnsavedUnload)
  }, [pageEditorDirty])

  const utils = trpc.useUtils()
  const { data: products, isLoading, error } = trpc.seo.products.list.useQuery()

  const seedMutation = trpc.seo.products.seed.useMutation({
    onError: (e) => toast.error(e.message || "Seed failed"),
    onSuccess: (res) => {
      toast.success(`Seeded: ${res.seeded.join(", ")}`)
      void utils.seo.products.list.invalidate()
    },
  })

  const activeProduct = products?.find((p) => p.key === productKey) ?? products?.[0]
  const resolvedKey = activeProduct?.key ?? SEO_DEFAULT_PRODUCT_KEY

  const changeTab = (tab: SeoTabValue) => {
    if (pageEditorDirty && tab !== "pages") {
      setPendingTab(tab)
      return
    }
    void setActiveTab(tab, { history: "push" })
  }
  const handleTabChange = (v: string) => changeTab(v as SeoTabValue)
  const openTab = (tab: SeoTabValue, slug?: string, mode: "inspect" | "preview" | "edit" = "inspect") => {
    if (pageEditorDirty && tab !== "pages") {
      setPendingTab(tab)
      return
    }
    if (slug) void setSelectedPageSlug(slug, { history: "push" })
    if (tab === "pages") void setPageMode(mode, { history: "push" })
    void setActiveTab(tab, { history: "push" })
  }

  useHotkeys("alt+1", () => openTab("cockpit"), { preventDefault: true, enableOnFormTags: false })
  useHotkeys("alt+2", () => openTab("operations"), { preventDefault: true, enableOnFormTags: false })
  useHotkeys("alt+3", () => openTab("pages"), { preventDefault: true, enableOnFormTags: false })
  useHotkeys("alt+4", () => openTab("templates"), { preventDefault: true, enableOnFormTags: false })
  useHotkeys("alt+5", () => openTab("keywords"), { preventDefault: true, enableOnFormTags: false })
  useHotkeys("alt+6", () => openTab("publish"), { preventDefault: true, enableOnFormTags: false })
  useHotkeys("alt+7", () => openTab("scoreboard"), { preventDefault: true, enableOnFormTags: false })
  useHotkeys(
    "shift+/",
    () => setShortcutLegendOpen(!shortcutLegendOpen),
    { preventDefault: true, enableOnFormTags: false },
    [shortcutLegendOpen],
  )
  useHotkeys("shift+h", () => setRunbookOpen(true), { preventDefault: true, enableOnFormTags: false })
  useHotkeys(
    "esc",
    () => {
      if (shortcutLegendOpen) setShortcutLegendOpen(false)
    },
    { enableOnFormTags: true },
    [shortcutLegendOpen],
  )

  return (
    <div className="flex h-full flex-col bg-background">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b-4 border-border bg-secondary p-4 md:p-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-primary md:text-3xl">
            SEO Control Center
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Review blockers, control releases, and understand performance without leaving the pipeline.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {products && products.length > 0 ? (
            <div className="flex gap-1">
              {products.map((p) => (
                <Button
                  key={p.key}
                  size="sm"
                  variant={p.key === resolvedKey ? "default" : "outline"}
                  onClick={() => void setProductKey(p.key)}
                  disabled={pageEditorDirty && p.key !== resolvedKey}
                  title={
                    pageEditorDirty && p.key !== resolvedKey
                      ? "Save or discard page changes before switching products"
                      : undefined
                  }
                >
                  {p.name ?? p.key}
                </Button>
              ))}
            </div>
          ) : null}
          <Button size="sm" variant="outline" onClick={() => seedMutation.mutate()} loading={seedMutation.isLoading}>
            Seed products
          </Button>
        </div>
      </div>

      {error ? (
        <div className="m-4 rounded border-2 border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load SEO products: {error.message}
          <div className="mt-2 text-muted-foreground">
            If the <code>seo_*</code> tables don&apos;t exist yet, run <code>pnpm db:push</code> for aixinternal, then{" "}
            <em>Seed products</em>.
          </div>
        </div>
      ) : isLoading ? (
        <div className="p-8 text-center text-muted-foreground">
          <div className="mb-2 inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
          <div>Loading products…</div>
        </div>
      ) : !products || products.length === 0 ? (
        <div className="m-4 rounded border-2 border-border bg-background p-8 text-center text-sm text-muted-foreground">
          No SEO products yet. Click <strong>Seed products</strong> above to create the managed products, then import
          keywords/pages via the <code>asd-aix-seo</code> skill.
        </div>
      ) : (
        <div className="flex-1 overflow-hidden">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="flex h-full flex-col">
            <div className="overflow-x-auto border-b-2 border-border bg-background px-2 md:px-6">
              <TabsList className="w-full flex-nowrap justify-start bg-transparent">
                <TabsTrigger value="cockpit" className={TAB_TRIGGER_CLASS} aria-keyshortcuts="Alt+1">
                  Cockpit
                </TabsTrigger>
                <TabsTrigger value="operations" className={TAB_TRIGGER_CLASS} aria-keyshortcuts="Alt+2">
                  Operations
                </TabsTrigger>
                <TabsTrigger value="pages" className={TAB_TRIGGER_CLASS} aria-keyshortcuts="Alt+3">
                  Pages
                </TabsTrigger>
                <TabsTrigger value="templates" className={TAB_TRIGGER_CLASS} aria-keyshortcuts="Alt+4">
                  Template Lab
                </TabsTrigger>
                <TabsTrigger value="keywords" className={TAB_TRIGGER_CLASS} aria-keyshortcuts="Alt+5">
                  Keywords
                </TabsTrigger>
                <TabsTrigger value="publish" className={TAB_TRIGGER_CLASS} aria-keyshortcuts="Alt+6">
                  Publish
                </TabsTrigger>
                <TabsTrigger value="scoreboard" className={TAB_TRIGGER_CLASS} aria-keyshortcuts="Alt+7">
                  Performance
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-hidden">
              <TabsContent value="cockpit" className="m-0 h-full">
                <SEOCockpitTab productKey={resolvedKey} productDomain={activeProduct?.domain} onOpenTab={openTab} />
              </TabsContent>
              <TabsContent value="operations" className="m-0 h-full">
                <SEOOperationsTab productKey={resolvedKey} />
              </TabsContent>
              <TabsContent value="pages" className="m-0 h-full">
                <SEOPagesTab
                  productKey={resolvedKey}
                  productDomain={activeProduct?.domain}
                  selectedSlug={selectedPageSlug}
                  mode={pageMode}
                  onSelectedSlugChange={(slug) => void setSelectedPageSlug(slug, { history: "push" })}
                  onModeChange={(mode) => void setPageMode(mode, { history: "push" })}
                  onDirtyChange={setPageEditorDirty}
                  shortcutGuideOpen={shortcutLegendOpen}
                />
              </TabsContent>
              <TabsContent value="templates" className="m-0 h-full">
                <SEOTemplateLabTab productKey={resolvedKey} />
              </TabsContent>
              <TabsContent value="keywords" className="m-0 h-full">
                <SEOKeywordsTab productKey={resolvedKey} />
              </TabsContent>
              <TabsContent value="publish" className="m-0 h-full">
                <SEOPublishTab productKey={resolvedKey} />
              </TabsContent>
              <TabsContent value="scoreboard" className="m-0 h-full">
                <SEOScoreboardTab productKey={resolvedKey} />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      )}
      <SEOShortcutLegend activeTab={activeTab} open={shortcutLegendOpen} onOpenChange={setShortcutLegendOpen} />
      <SEORunbookDrawer activeTab={activeTab} open={runbookOpen} onOpenChange={setRunbookOpen} />
      <AlertDialog open={!!pendingTab} onOpenChange={(open) => !open && setPendingTab(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave the page editor?</AlertDialogTitle>
            <AlertDialogDescription>
              Unsaved changes are only stored in this browser view. Save a revision or explicitly discard them before
              switching SEO workflows.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!pendingTab) return
                setPageEditorDirty(false)
                void setPageMode("inspect", { history: "push" })
                void setActiveTab(pendingTab, { history: "push" })
                setPendingTab(null)
              }}
            >
              Discard and leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
